import React, { createContext, useContext, useMemo, useState } from "react";

type AreaExpansionContextValue = {
  isExpanded: (id: string) => boolean;
  toggle: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  anyCollapsed: boolean;
  hasCollapsibleAreas: boolean;
};

const AreaExpansionContext = createContext<AreaExpansionContextValue | null>(
  null
);

type ProviderProps = {
  collapsibleIds: string[];
  children: React.ReactNode;
};

export function AreaExpansionProvider({
  collapsibleIds,
  children,
}: ProviderProps) {
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());

  const value = useMemo<AreaExpansionContextValue>(() => {
    const hasCollapsibleAreas = collapsibleIds.length > 0;
    const anyCollapsed = collapsibleIds.some((id) => collapsedIds.has(id));

    return {
      isExpanded: (id) => !collapsedIds.has(id),
      toggle: (id) =>
        setCollapsedIds((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        }),
      expandAll: () => setCollapsedIds(new Set()),
      collapseAll: () => setCollapsedIds(new Set(collapsibleIds)),
      anyCollapsed,
      hasCollapsibleAreas,
    };
  }, [collapsedIds, collapsibleIds]);

  return (
    <AreaExpansionContext.Provider value={value}>
      {children}
    </AreaExpansionContext.Provider>
  );
}

export function useAreaExpansion() {
  const ctx = useContext(AreaExpansionContext);
  if (!ctx) {
    throw new Error(
      "useAreaExpansion must be used within an AreaExpansionProvider"
    );
  }
  return ctx;
}
